import * as authApi from '../api/modules/auth'
import * as groupsApi from '../api/modules/groups'
import * as ordersApi from '../api/modules/orders'
import { HttpStatuses } from '../constants/komegura-config'
import { isSuccessStatus } from '../utils/http'

// 認証関係のストアモジュール
export default {
  namespaced: true,
  state: {
    // APIアクセス用トークン
    accessToken: null,
    // 自動リフレッシュ用タイマーID
    timerId: null,
    // 認証ユーザー
    authUser: null,
    // 顧客グループ情報
    groupInfo: null,
    // 顧客グループ所属メンバー
    groupMembers: null,
    // 注文履歴
    orderHistories: null,
    // アドレス帳
    addressLists: null,
  },
  getters: {
    accessToken: state => state.accessToken,
    timerId: state => state.timerId,
    authUser: state => state.authUser,
    groupInfo: state => state.groupInfo,
    groupMembers: state => state.groupMembers,
    orderHistories: state => state.orderHistories,
    latestOrder: state => {
      if (state.orderHistories && state.orderHistories.length > 0) {
        // 現状はAPI側でソートされてるので以下のようにindexが0の要素を返すだけでもいけるが一応判定する。。。
        // latestOrder: state => state.orderHistories ? state.orderHistories[0] : null,
        // これも注文IDだけの判断でも良いかもしれないが一応注文日から最新注文を判断する
        return state.orderHistories.reduce((a, b) =>
          a.order_date > b.order_date ? a :
            a.order_date < b.order_date ? b :
              a.id > b.id ? a : b)
      }
      else {
        return null
      }
    },
    addressLists: state => state.addressLists,
  },
  mutations: {
    updateAccessToken(state, token) {
      state.accessToken = token
    },
    updateTimerId(state, id) {
      state.timerId = id
    },
    updateAuthUser(state, authUser) {
      state.authUser = authUser
    },
    updateGroupInfo(state, groupInfo) {
      state.groupInfo = groupInfo
    },
    updateGroupMembers(state, groupMembers) {
      state.groupMembers = groupMembers
    },
    // メンバーを取り除く（画面表示用）
    removeGroupMember(state, id) {
      const index = state.groupMembers.findIndex(member => member.id == id)
      const isExist = index != -1

      if (isExist) {
        state.groupMembers.splice(index, 1)
      }
    },
    updateOrderHistories(state, histories) {
      state.orderHistories = histories
    },
    // 注文履歴を取り除く（画面表示用）
    removeOrderHistory(state, id) {
      const index = state.orderHistories.findIndex(history => history.id == id)
      const isExist = index != -1

      if (isExist) {
        state.orderHistories.splice(index, 1)
      }
    },
    updateAddressLists(state, lists) {
      state.addressLists = lists
    },
  },
  actions: {
    // ログイン
    async login({ dispatch }, authData) {

      const result = await authApi.login(authData)

      if (isSuccessStatus(result.statusCode)) {
        // ログイン成功
        const data = result.response
        dispatch('setAuthData', {
          access_token: data.access_token,
          expires_in: data.expires_in
        })
      }
      else if (result.statusCode === HttpStatuses.UNAUTHORIZED) {
        throw new Error('メールアドレスまたはパスワードが正しくありません。')
      }
      else {
        dispatch('error/setError', { code: result.statusCode, id: result.requestId, message: result.response }, { root: true })
      }
    },
    // 新規アカウント作成
    async signup({ dispatch }, authData) {

      const result = await authApi.signup(authData)

      if (isSuccessStatus(result.statusCode)) {
        // アカウント作成成功（そのままログインする）
        // await dispatch('login', authData);
        return true
      }
      else if (result.statusCode === HttpStatuses.CONFLICT) {
        throw new Error('既に使用されているメールアドレスです。')
      }
      else {
        dispatch('error/setError', { code: result.statusCode, id: result.requestId, message: result.response }, { root: true })
      }
      return false
    },
    // トークン再生成
    async refreshToken({ dispatch }, token) {

      const result = await authApi.refreshToken(token)

      if (isSuccessStatus(result.statusCode)) {
        // トークン再生成成功
        const data = result.response
        dispatch('setAuthData', {
          access_token: data.access_token,
          expires_in: data.expires_in
        })
      }
      else if (result.statusCode === HttpStatuses.UNAUTHORIZED) {
        throw new Error('トークンのリフレッシュ期限切れです。')
      }
      else {
        dispatch('error/setError', { code: result.statusCode, id: result.requestId, message: result.response }, { root: true })
      }
    },
    // 認証情報セット
    setAuthData({ commit, dispatch }, authData) {
      const now = new Date()
      const expiryTimeMs = now.getTime() + authData.expires_in * 1000

      commit('updateAccessToken', authData.access_token)
      dispatch('setAuthUser', authData.access_token)

      // アクセストークンと有効期限はローカルストレージに保存
      localStorage.setItem('accessToken', authData.access_token)
      localStorage.setItem('expiryTimeMs', expiryTimeMs)

      const id = setTimeout(() => {
        dispatch('refreshToken', authData.access_token)
      }, authData.expires_in * 1000)
      commit('updateTimerId', id)
    },
    // 認証ユーザーセット
    async setAuthUser({ commit, dispatch }, token) {

      const result = await authApi.fetchMe(token)

      if (isSuccessStatus(result.statusCode)) {
        commit('updateAuthUser', result.response)
        localStorage.setItem('authUser', JSON.stringify(result.response))
      }
      else if (result.statusCode === HttpStatuses.UNAUTHORIZED) {
        throw new Error('ログイン情報を取得できませんでした。')
      }
      else {
        dispatch('error/setError', { code: result.statusCode, id: result.requestId, message: result.response }, { root: true })
      }
    },
    // ログアウト
    async logout({ dispatch, getters }) {
      // 自動トークンリフレッシュを解除
      clearInterval(getters.timerId)

      const result = await authApi.logout(getters.accessToken)

      if (isSuccessStatus(result.statusCode)) {
        dispatch('clearAuth')
      }
      else if (result.statusCode === HttpStatuses.UNAUTHORIZED) {
        throw new Error('ログアウト時にエラーが発生しました。')
      }
      else {
        dispatch('error/setError', { code: result.statusCode, id: result.requestId, message: result.response }, { root: true })
      }
    },
    // 自動ログイン
    async autoLogin({ commit, dispatch }) {
      const accessToken = localStorage.getItem('accessToken')
      if (!accessToken) return

      const authUser = JSON.parse(localStorage.getItem('authUser'))
      if (authUser) commit('updateAuthUser', authUser)

      const now = new Date()
      const expiryTimeMs = localStorage.getItem('expiryTimeMs')
      const isExpired = now.getTime() >= expiryTimeMs
      console.log(now.getTime() + ' ' + expiryTimeMs)

      if (isExpired) {
        // 有効期限切れ
        try {
          await dispatch('refreshToken', accessToken)
        }
        catch (err) {
          // リフレッシュの期限も切れていた場合（ログイン情報を破棄）
          dispatch('clearAuth')
        }
      }
      else {
        // 有効期限内
        const expiresInMs = expiryTimeMs - now.getTime()

        commit('updateAccessToken', accessToken)
        dispatch('setAuthUser', accessToken)

        // 有効期限が切れるタイミングでトークン再生成
        const id = setTimeout(() => {
          dispatch('refreshToken', accessToken)
        }, expiresInMs)
        commit('updateTimerId', id)
      }
    },
    clearAuth({ commit }) {
      commit('updateAccessToken', null)
      commit('updateAuthUser', null)
      localStorage.removeItem('authUser')
      localStorage.removeItem('accessToken')
      localStorage.removeItem('expiryTimeMs')
    },
    // 顧客グループ情報取得
    async loadGroup({ commit, dispatch, getters }) {

      const gid = getters.authUser.customer_group_id
      const result = await groupsApi.fetchGroup({ groupId: gid, accessToken: getters.accessToken })

      if (isSuccessStatus(result.statusCode)) {
        commit('updateGroupInfo', result.response)
      }
      else {
        dispatch('error/setError', { code: result.statusCode, id: result.requestId, message: result.response }, { root: true })
      }
    },
    // 顧客グループ情報クリア
    clearGroup({ commit }) {
      commit('updateGroupInfo', null)
    },
    // 顧客グループ所属メンバー取得
    async loadGroupMembers({ commit, dispatch, getters }) {

      const gid = getters.authUser.customer_group_id
      const result = await groupsApi.fetchGroupMembers({ groupId: gid, accessToken: getters.accessToken })

      if (isSuccessStatus(result.statusCode)) {
        commit('updateGroupMembers', result.response)
      }
      else {
        dispatch('error/setError', { code: result.statusCode, id: result.requestId, message: result.response }, { root: true })
      }
    },
    // 顧客グループ所属メンバークリア
    clearGroupMembers({ commit }) {
      commit('updateGroupMembers', null)
    },
    // 新規アカウント作成
    async registerMember({ dispatch, getters }, authData) {

      const result = await authApi.registerMember({ authData, accessToken: getters.accessToken })

      if (isSuccessStatus(result.statusCode)) {
        // アカウント作成成功（メンバー更新）
        // await dispatch('loadGroupMembers');
        return true
      }
      else if (result.statusCode === HttpStatuses.CONFLICT) {
        throw new Error('既に使用されているメールアドレスです。')
      }
      else {
        dispatch('error/setError', { code: result.statusCode, id: result.requestId, message: result.response }, { root: true })
      }
      return false
    },
    // メンバー削除
    async removeMember({ commit, dispatch, getters }, id) {

      const result = await authApi.removeMember({ id, accessToken: getters.accessToken })

      if (isSuccessStatus(result.statusCode)) {
        // APIリクエストを減らすために表示用のデータを直接削除（画面側ではデータ再取得しない）
        commit('removeGroupMember', id)
      }
      else {
        dispatch('error/setError', { code: result.statusCode, id: result.requestId, message: result.response }, { root: true })
      }
    },
    // 注文履歴取得
    async loadOrderHistories({ commit, dispatch, getters }) {

      const result = await ordersApi.listOrdersByCustomer({ customerId: getters.authUser.id, accessToken: getters.accessToken, descending: true })

      if (isSuccessStatus(result.statusCode)) {
        commit('updateOrderHistories', result.response)
      }
      else {
        dispatch('error/setError', { code: result.statusCode, id: result.requestId, message: result.response }, { root: true })
      }
    },
    // 注文履歴クリア
    clearOrderHistories({ commit }) {
      commit('updateOrderHistories', null)
    },
    // 注文キャンセル
    async orderCancel({ commit, dispatch }, id) {

      const result = await ordersApi.cancelOrder(id)

      if (isSuccessStatus(result.statusCode)) {
        // APIリクエストを減らすために表示用のデータを直接削除（画面側ではデータ再取得しない）
        commit('removeOrderHistory', id)
      }
      else if (result.statusCode === HttpStatuses.BAD_REQUEST) {
        throw new Error(result.response)
      }
      else {
        dispatch('error/setError', { code: result.statusCode, id: result.requestId, message: result.response }, { root: true })
      }
    },
    // アドレス帳取得
    async loadAddressLists({ commit, dispatch, getters }, id) {

      const result = await authApi.fetchAddressLists({ customerId: id, accessToken: getters.accessToken })

      if (isSuccessStatus(result.statusCode)) {
        commit('updateAddressLists', result.response)
      }
      else {
        dispatch('error/setError', { code: result.statusCode, id: result.requestId, message: result.response }, { root: true })
      }
    },
    // アドレス帳クリア
    clearAddressLists({ commit }) {
      commit('updateAddressLists', null)
    },
  },
}
